import { faSparkles } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';

import type { TVisitTreatment } from '../../../../react-query/useVisitSummaries';
import { VideaLogoIcon } from '../../../../svgs/videaLogoIcon';
import { useLocalizedFormatDate } from '../../../claims/summary/state';
import { ImplantAndAlignerText } from '../../ImplantAndAlignerText';

import { BasicOdontogramRecommendations } from './BasicOdontogramRecommendations';
import { CompletedTreatmentBadge } from './completedTreatmentBadge';
import { useIsBasicOdontogram } from './hooks';
import { ScheduledTreatmentBadge } from './scheduledTreatmentBadge';
import type { TProcedureWithAddressed } from './utils';
import {
  getDoesTreatmentAddressRecommendation,
  useGetRecommendationName,
} from './utils';

export const HoverInfo = ({
  scheduledTreatments,
  notScheduledTreatments,
  completedTreatments,
  aiFindingsList,
  recommendedProcedures,
  module,
}: {
  scheduledTreatments?: TVisitTreatment[];
  notScheduledTreatments?: TVisitTreatment[];
  completedTreatments?: TVisitTreatment[];
  aiFindingsList?: string[];
  recommendedProcedures?: TProcedureWithAddressed[];
  module?: 'image-viewer' | 'patient-list';
}) => {
  const isBasicOdontogram = useIsBasicOdontogram();
  const formatDate = useLocalizedFormatDate();
  const { getRecommendationName } = useGetRecommendationName(true);
  const { t } = useLingui();

  const processAiFinding = (finding: string): string => {
    // Check if the finding matches "DMF: X%" format
    const dmfMatch = finding.match(/^DMF:\s*(\d+%)$/);
    if (dmfMatch) {
      const percentage = dmfMatch[1];
      return `${t`DMF`}: ${percentage}`;
    }
    return finding;
  };

  const getUniqueTreatments = (treatments?: TVisitTreatment[]) => {
    type TTreatmentCounts = TVisitTreatment & {
      count: number;
    };

    const initUniqueScheduledTreatments: TTreatmentCounts[] = [];
    return treatments?.reduce((acc, val) => {
      const matchingTx = acc.find(
        (tx) =>
          tx.txGroupLongName.en === val.txGroupLongName.en &&
          tx.txCode === val.txCode &&
          tx.status === val.status &&
          (tx.status === 'scheduled_today' ||
            tx.status === 'completed' ||
            formatDate(tx.txDate || undefined) ===
              formatDate(val.txDate || undefined)),
      );
      if (matchingTx) {
        matchingTx.count += 1;
      } else {
        acc.push({ ...val, count: 1 });
      }
      return acc;
    }, initUniqueScheduledTreatments);
  };

  const uniqueScheduledTreatments = getUniqueTreatments(scheduledTreatments);
  const uniqueNotScheduledTreatments = getUniqueTreatments(
    notScheduledTreatments,
  );

  const uniqueCompletedTreatments = getUniqueTreatments(completedTreatments);

  const hasAddressedRecommendation = (recommendedProcedures ?? []).some(
    ({ isAddressed }) => isAddressed,
  );

  if (isBasicOdontogram) {
    return (
      <BasicOdontogramRecommendations
        recommendedProcedures={recommendedProcedures}
      />
    );
  }

  return (
    <div className="vai-flex vai-flex-row vai-gap-6 vai-w-max">
      {!!(
        uniqueScheduledTreatments?.length ||
        uniqueNotScheduledTreatments?.length ||
        uniqueCompletedTreatments?.length
      ) && (
        <div className="vai-flex vai-flex-col vai-gap-2">
          {!!uniqueScheduledTreatments?.length && (
            <>
              <span className="vai-text-sm vai-font-semibold">
                <Trans>Scheduled Treatments</Trans>
              </span>
              <ul className="vai-m-0 vai-pl-0 vai-flex vai-flex-col vai-gap-1 vai-list-disc">
                {uniqueScheduledTreatments?.map((treatment) => (
                  <li className="vai-list-none" key={treatment.txCode}>
                    <span className="vai-flex vai-flex-row vai-items-center vai-gap-1">
                      <ScheduledTreatmentBadge
                        isDarkMode={module === 'image-viewer'}
                        scheduledToday={treatment.status === 'scheduled_today'}
                        scheduledDate={
                          treatment.txDate
                            ? format(parseISO(treatment.txDate), 'M/dd/yyyy')
                            : ''
                        }
                      />

                      <span className="vai-text-sm">
                        {/* TODO: Placeholder for treatment name, what do we use here? */}
                        {treatment.txGroupLongName.en} {treatment.txCode}{' '}
                        {treatment.count > 1 ? `(${treatment.count})` : ''}
                      </span>
                    </span>

                    {getDoesTreatmentAddressRecommendation(
                      treatment,
                      recommendedProcedures,
                    ) && (
                      <p className="vai-text-sm vai-max-w-60 vai-text-gray-400 vai-mt-1 vai-ml-1 vai-list-item">
                        <Trans>Addresses an AI recommendation.</Trans>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {!!uniqueCompletedTreatments?.length && (
            <>
              <span className="vai-text-sm vai-font-semibold">
                <Trans>Completed Treatments</Trans>
              </span>
              <ul className="vai-m-0 vai-pl-0 vai-flex vai-flex-col vai-gap-1 vai-list-disc">
                {uniqueCompletedTreatments?.map((treatment) => (
                  <li className="vai-list-none" key={treatment.txCode}>
                    <span className="vai-flex vai-flex-row vai-items-center vai-gap-1">
                      <CompletedTreatmentBadge
                        completed={treatment.status === 'completed'}
                        completedDate={treatment.txDate || ''}
                      />

                      <span className="vai-text-sm">
                        {/* TODO: Placeholder for treatment name, what do we use here? */}
                        {treatment.txGroupLongName.en} {treatment.txCode}{' '}
                        {treatment.count > 1 ? `(${treatment.count})` : ''}
                      </span>
                    </span>

                    {getDoesTreatmentAddressRecommendation(
                      treatment,
                      recommendedProcedures,
                    ) && (
                      <p className="vai-text-sm vai-max-w-60 vai-text-gray-400 vai-mt-1 vai-ml-1 vai-list-item">
                        <Trans>Addresses an AI recommendation.</Trans>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {!!uniqueNotScheduledTreatments?.length && (
            <>
              <span className="vai-text-sm vai-font-semibold">
                <Trans>Not Scheduled</Trans>
              </span>
              <ul className="vai-m-0 vai-pl-4 vai-list-disc">
                {uniqueNotScheduledTreatments.map((treatment) => (
                  <li key={treatment.txCode}>
                    <span className="vai-text-sm">
                      {/* TODO: Placeholder for treatment name, what do we use here? */}
                      {treatment.txGroupLongName.en} {treatment.txCode}{' '}
                      {treatment.count > 1 ? `(${treatment.count})` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {!!recommendedProcedures?.length && (
        <div className="vai-flex vai-flex-col vai-gap-2 vai-w-max">
          <span className="vai-flex vai-align-middle vai-gap-2 vai-text-sm vai-font-semibold">
            <Trans>
              <VideaLogoIcon /> Recommendations
            </Trans>
          </span>
          <ul className="vai-m-0 vai-pl-4 vai-list-disc">
            {recommendedProcedures?.map((procedure) => (
              <li key={procedure.name}>
                <div className="vai-flex vai-flex-col">
                  <span
                    className={classNames('vai-text-sm', {
                      'vai-text-gray-400': procedure.isAddressed,
                    })}
                  >
                    {getRecommendationName(procedure.name)}
                    {procedure.isAddressed && <> &#42;</>}
                  </span>
                  <ImplantAndAlignerText
                    gapDist={procedure.gapDist}
                    hasCrowding={procedure.hasCrowding}
                    name={procedure.name}
                    summedToothSpacing={procedure.summedToothSpacing}
                  />
                </div>
              </li>
            ))}
          </ul>

          {hasAddressedRecommendation && (
            <p className="vai-text-sm vai-max-w-60">
              &#42;{' '}
              <Trans>Addressed by a scheduled or completed treatment.</Trans>
            </p>
          )}
        </div>
      )}
      {!!aiFindingsList?.length && (
        <div className="vai-flex vai-flex-col vai-gap-2 vai-w-max">
          <span className="vai-flex vai-align-middle vai-gap-2 vai-text-sm vai-font-semibold">
            <Trans>
              <FontAwesomeIcon icon={faSparkles} /> AI Findings
            </Trans>
          </span>
          <ul className="vai-m-0 vai-pl-4 vai-list-disc">
            {aiFindingsList.map((aiFinding) => (
              <li key={aiFinding}>
                <span className="vai-text-sm">
                  {processAiFinding(aiFinding)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
